/**
 * Iter 187 — Chart Scanner page.
 *
 * React-side wrapper around `POST /api/scanner/analyze` + `GET /api/scanner/history`.
 * Pulls recent bars from `/api/historical/candles/{symbol}/{timeframe}` so the
 * same scanner that the Tampermonkey tab uses can run against cached Mongo
 * bars when the operator is not sitting in front of a Pocket Option tab.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, ScanSearch, TrendingUp, TrendingDown, Minus,
         Clock, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_ASSETS = [
  "EURUSD_OTC", "AUDCAD_OTC", "USDJPY_OTC", "GBPUSD_OTC",
  "AUDUSD_OTC", "EURJPY_OTC", "USDCHF_OTC", "USDCAD_OTC",
];
const TIMEFRAMES = ["5s", "15s", "30s", "1m", "5m", "15m"];

function DirectionPill({ direction, confidence, quality }) {
  const Icon = direction === "BUY" ? TrendingUp
             : direction === "SELL" ? TrendingDown
             : Minus;
  const colorBg = direction === "BUY" ? "bg-emerald-900/60 text-emerald-200 border-emerald-500/60"
                 : direction === "SELL" ? "bg-rose-900/60 text-rose-200 border-rose-500/60"
                 : "bg-slate-800 text-slate-300 border-slate-600";
  return (
    <div className={`flex items-center gap-3 px-4 py-2 rounded-full border ${colorBg}`}
         data-testid="scanner-direction-pill">
      <Icon className="h-5 w-5" />
      <span className="font-black tracking-wider">{direction}</span>
      <span className="text-xs opacity-80">· {quality}</span>
      <span className="text-xs font-mono ml-1" data-testid="scanner-confidence">
        {(confidence * 100).toFixed(1)}%
      </span>
    </div>
  );
}

function ConfidenceBar({ value }) {
  const pct = Math.max(0, Math.min(100, value * 100));
  return (
    <div className="h-2 bg-slate-800 rounded overflow-hidden">
      <div className="h-full bg-amber-500 transition-all duration-500"
           style={{ width: `${pct}%` }}
           data-testid="scanner-confidence-bar" />
    </div>
  );
}

export default function ChartScanner() {
  const [asset, setAsset] = useState("EURUSD_OTC");
  const [assetList, setAssetList] = useState(FALLBACK_ASSETS);
  const [assetQuery, setAssetQuery] = useState("");
  const [timeframe, setTimeframe] = useState("5s");
  const [expiry, setExpiry] = useState(60);
  const [includeEnsemble, setIncludeEnsemble] = useState(true);
  const [includeLLM, setIncludeLLM] = useState(true);
  const [loading, setLoading] = useState(false);
  const [barCount, setBarCount] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Fetch the full PO asset catalog on mount (OTC + regular), fall back to the
  // short hard-coded list only if the endpoint is unreachable.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/assets/symbols`);
        const otc = Array.isArray(r.data?.otc_symbols) ? r.data.otc_symbols : [];
        const reg = Array.isArray(r.data?.regular_symbols) ? r.data.regular_symbols : [];
        const combined = [
          ...otc.map((s) => (s.endsWith("_OTC") ? s : `${s}_OTC`)),
          ...reg,
        ];
        const seen = new Set();
        const uniq = combined.filter((s) => s && !seen.has(s) && seen.add(s));
        if (!dead && uniq.length > 0) setAssetList(uniq.sort());
      } catch (_e) {
        // keep fallback
      }
    })();
    return () => { dead = true; };
  }, []);

  /* ───────── bar fetch ───────── */
  async function fetchBars() {
    try {
      const url = `${API}/historical/candles/${encodeURIComponent(asset)}/${encodeURIComponent(timeframe)}`;
      const res = await axios.get(url, { params: { limit: 100 } });
      const bars = (res.data?.candles || res.data || []).slice(-100).map((b) => ({
        open: Number(b.open ?? 0), high: Number(b.high ?? 0),
        low:  Number(b.low ?? 0),  close: Number(b.close ?? 0),
        volume: Number(b.volume ?? 0),
        timestamp: b.timestamp || null,
      })).filter((b) => b.open > 0 && b.close > 0);
      return bars;
    } catch (e) {
      return [];
    }
  }

  async function runScan() {
    setLoading(true); setError(null); setResult(null);
    try {
      const bars = await fetchBars();
      setBarCount(bars.length);
      if (bars.length < 5) {
        setError(`Only ${bars.length} bars found for ${asset} @ ${timeframe}. Need ≥5.`);
        return;
      }
      const r = await axios.post(`${API}/scanner/analyze`, {
        asset, timeframe, bars, expiry_seconds: expiry,
        include_ensemble: includeEnsemble,
        include_llm: includeLLM,
        image_base64: null,
      });
      setResult(r.data);
      await loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "scan failed");
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    try {
      const r = await axios.get(`${API}/scanner/history`, { params: { limit: 10 } });
      setHistory(r.data?.items || []);
    } catch (_e) { /* ignore */ }
  }

  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const h = setInterval(() => { if (!loading) runScan(); }, 30000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, asset, timeframe, expiry, includeEnsemble, includeLLM]);

  const bullets = useMemo(() => result?.bullets || [], [result]);

  return (
    <div className="p-6 space-y-6" data-testid="chart-scanner-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-100 flex items-center gap-3">
            <ScanSearch className="h-8 w-8 text-amber-500" />
            Chart Scanner
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            AI-driven technical read-out on the last 100 cached bars. BUY / SELL with narrative + S/R + patterns.
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-slate-200">Scan Parameters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">
                Asset <span className="text-slate-600">({assetList.length} available)</span>
              </label>
              <input data-testid="scanner-asset-filter"
                     placeholder="Filter · e.g. EUR, BTC, AAPL"
                     value={assetQuery}
                     onChange={(e) => setAssetQuery(e.target.value)}
                     className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 mb-1 text-xs text-slate-100 placeholder:text-slate-500" />
              <select data-testid="scanner-asset-select"
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-slate-100"
                      value={asset} onChange={(e) => setAsset(e.target.value)}>
                {assetList
                  .filter((a) => !assetQuery || a.toUpperCase().includes(assetQuery.toUpperCase()))
                  .slice(0, 500)
                  .map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Timeframe</label>
              <select data-testid="scanner-tf-select"
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-slate-100"
                      value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Expiry (s)</label>
              <select data-testid="scanner-expiry-select"
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-slate-100"
                      value={expiry} onChange={(e) => setExpiry(Number(e.target.value))}>
                {[15, 30, 60, 120, 300].map((v) => <option key={v} value={v}>{v}s</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button data-testid="scanner-run-btn"
                      onClick={runScan} disabled={loading}
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ScanSearch className="h-4 w-4 mr-2" />}
                {loading ? "Scanning…" : "Run Scan"}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4 pt-1 text-xs text-slate-400 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeEnsemble}
                     onChange={(e) => setIncludeEnsemble(e.target.checked)}
                     data-testid="scanner-chk-ensemble" />
              GO-max-accuracy ensemble
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeLLM}
                     onChange={(e) => setIncludeLLM(e.target.checked)}
                     data-testid="scanner-chk-llm" />
              LLM narrative
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={autoRefresh}
                     onChange={(e) => setAutoRefresh(e.target.checked)}
                     data-testid="scanner-chk-auto" />
              auto-scan every 30s
            </label>
            {barCount > 0 && (
              <span className="text-slate-500">· {barCount} bars loaded</span>
            )}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="bg-rose-900/40 border-rose-700">
          <CardContent className="p-4 text-rose-200 text-sm" data-testid="scanner-error">
            {error}
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="bg-slate-900/60 border-slate-800" data-testid="scanner-result-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <DirectionPill direction={result.direction}
                             confidence={result.confidence}
                             quality={result.quality} />
              <div className="flex items-center gap-3 text-xs text-slate-500">
                {result.vision_used
                  ? <Badge variant="outline" className="border-amber-500 text-amber-400"><Eye className="h-3 w-3 mr-1"/>vision</Badge>
                  : <Badge variant="outline" className="border-slate-600 text-slate-500"><EyeOff className="h-3 w-3 mr-1"/>no vision</Badge>}
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{result.latency_ms}ms</span>
              </div>
            </div>
            <ConfidenceBar value={result.confidence || 0} />

            {result.narrative && (
              <div className="p-3 bg-slate-800/60 rounded border-l-2 border-amber-500 text-sm text-slate-200 leading-relaxed"
                   data-testid="scanner-narrative-box">
                {result.narrative}
              </div>
            )}

            {bullets.length > 0 && (
              <ul className="space-y-1.5 text-sm text-slate-300 pl-5 list-disc"
                  data-testid="scanner-bullets-list">
                {bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="p-3 bg-slate-800/50 rounded">
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Resistance</div>
                <div className="font-mono text-sm text-rose-300">
                  {(result.resistance || []).join(" · ") || "—"}
                </div>
              </div>
              <div className="p-3 bg-slate-800/50 rounded">
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Support</div>
                <div className="font-mono text-sm text-emerald-300">
                  {(result.support || []).join(" · ") || "—"}
                </div>
              </div>
              <div className="p-3 bg-slate-800/50 rounded">
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Patterns</div>
                <div className="font-mono text-xs text-slate-300">
                  {(result.patterns || []).join(", ") || "none"}
                </div>
              </div>
            </div>

            {result.ensemble && (
              <div className="p-3 bg-slate-800/50 rounded text-xs text-slate-400">
                <span className="text-slate-500">Ensemble: </span>
                {result.ensemble.ok
                  ? (result.ensemble.fire
                    ? <>fires <b className="text-slate-200">{result.ensemble.direction}</b> @ {(result.ensemble.confidence * 100).toFixed(1)}% ({(result.ensemble.agreeing || []).join(", ") || "—"})</>
                    : <>no-fire · {result.ensemble.reason}</>)
                  : "unavailable"}
              </div>
            )}

            <div className="text-xs text-slate-500 flex gap-2 flex-wrap pt-1 border-t border-slate-800">
              <span>sources:</span>
              {(result.sources_used || []).map((s) => (
                <Badge key={s} variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{s}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-300">Recent Scans</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {history.map((h) => (
                <div key={h.scan_id}
                     className="flex items-center justify-between text-xs py-1.5 border-b border-slate-800/50 last:border-0"
                     data-testid={`scanner-hist-${h.scan_id}`}>
                  <div className="flex items-center gap-3">
                    <Badge className={
                      h.direction === "BUY"  ? "bg-emerald-900/60 text-emerald-300"
                    : h.direction === "SELL" ? "bg-rose-900/60 text-rose-300"
                    : "bg-slate-800 text-slate-400"
                    }>{h.direction}</Badge>
                    <span className="font-mono text-slate-300">{h.asset}</span>
                    <span className="text-slate-500">{h.timeframe}</span>
                    <span className="text-slate-500">{(h.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <span className="text-slate-600 text-[10px]">
                    {new Date(h.created_at).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
